import { styled, Typography as MuiTypography } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { CSSProperties, ForwardedRef, forwardRef, ReactNode } from 'react'

type Variant = keyof typeof VARIANT_MAPPING

export type TypographyProps = {
    children: ReactNode
    variant: Variant
    color?: string
    noWrap?: boolean
    lineClamp?: number
    className?: string
    bold?: boolean
    breakLongWords?: boolean
    id?: string
    fontStyle?: CSSProperties['fontStyle']
}

const VARIANT_MAPPING = {
    headline1: 'h1',
    headline2: 'h2',
    headline3: 'h3',
    headline4: 'h4',
    headline5: 'h5',
    textM: 'p',
    textLinkM: 'span',
    textS: 'p',
    textLinkS: 'span',
    textXS: 'p',
    graphLabel: 'p',
    link: 'p',
    linkCaps: 'p',
    valueL: 'p',
    valueM: 'p',
    valueS: 'p',
}

export const Typography = forwardRef(
    (
        { children, variant = 'textM', color, noWrap, lineClamp, bold, fontStyle, breakLongWords, className, id }: TypographyProps,
        ref: ForwardedRef<HTMLDivElement>
    ) => {
        return (
            <StyledTypography
                ref={ref}
                noWrap={noWrap}
                variant={variant}
                color={color}
                variantMapping={VARIANT_MAPPING}
                $lineClamp={lineClamp}
                className={className}
                $bold={bold}
                $fontStyle={fontStyle}
                $breakLongWords={breakLongWords}
                id={id}
            >
                {children}
            </StyledTypography>
        )
    }
)

const StyledTypography = styled(MuiTypography, {
    shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $lineClamp?: number; $bold?: boolean; $fontStyle?: CSSProperties['fontStyle']; $breakLongWords?: boolean }>(
    ({ $lineClamp, $bold, $fontStyle, $breakLongWords }) => ({
        '&.MuiTypography-headline1': tokens.Headline1,
        '&.MuiTypography-headline2': tokens.Headline2,
        '&.MuiTypography-headline3': tokens.Headline3,
        '&.MuiTypography-headline4': tokens.Headline4,
        '&.MuiTypography-headline5': tokens.Headline5,
        '&.MuiTypography-textM': tokens.TextM,
        '&.MuiTypography-textLinkM': tokens.TextlinkM,
        '&.MuiTypography-textS': tokens.TextS,
        '&.MuiTypography-textLinkS': tokens.TextlinkS,
        '&.MuiTypography-graphLabel': tokens.GraphLabel,
        '&.MuiTypography-link': tokens.Link,
        '&.MuiTypography-linkCaps': tokens.LinkCaps,
        '&.MuiTypography-valueL': tokens.ValueL,
        '&.MuiTypography-valueM': tokens.ValueM,
        '&.MuiTypography-valueS': tokens.ValueS,
        '&.MuiTypography-textXS': tokens.TextXs,
        ...($breakLongWords ? { overflowWrap: 'anywhere' } : {}),
        ...getLineClampStyles($lineClamp),
        ...($bold ? { fontWeight: '500!important' } : {}),
        ...($fontStyle ? { fontStyle: `${$fontStyle}!important` } : {}),
    })
)

type LineClampStyles = Pick<CSSProperties, 'overflow' | 'display' | 'WebkitLineClamp' | 'lineClamp' | 'WebkitBoxOrient'>

function getLineClampStyles(lineClamp?: number): LineClampStyles | undefined {
    if (lineClamp === undefined) {
        return
    }
    return {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: `${lineClamp}`,
        lineClamp: `${lineClamp}`,
        WebkitBoxOrient: 'vertical',
    }
}

export default Typography
